import { IndentValues } from "@/lib/utils/IndentValues";
import { SpaceValues } from "@/lib/utils/SpaceValues";
import { DateRangeDualTextInput } from "@stenajs-webui/calendar";
import { Box, Indent, Row, Space, Text, Txt } from "@stenajs-webui/core";
import { CardBody, Label } from "@stenajs-webui/elements";
import { FieldGroupCaption } from "@/components/forms/FieldGroupCaption";
import {
  CheckboxWithLabel,
  RadioButtonWithLabel,
  TextArea,
  TextInput,
} from "@stenajs-webui/forms";
import { cssColor } from "@stenajs-webui/theme";
import * as React from "react";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { CurrencyRadioButtonRow } from "@/components/data-driven-inputs/radio-buttons/CurrencyRadioButtonRow";
import { RouteInRoutePairSelect } from "@/components/data-driven-inputs/selects/route-in-pair-select/RouteInRoutePairSelect";
import { StoreState } from "@/redux/RootReducer";
import { EditRouteAgreementFormModel } from "../types/EditRouteAgreementFormModel";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import {
  HeadAgreementStatusCode,
  RouteAgreementDetailsStateFragment,
  RouteAgreementTypeCode,
} from "@/gql/graphql";
import { ZIndex } from "@/lib/utils/ZIndexEnum";

interface Props {
  value: EditRouteAgreementFormModel;
  setFields: (value: Partial<EditRouteAgreementFormModel>) => void;
  disableType: boolean;
  children?: React.ReactNode;
  routeAgreement?: RouteAgreementDetailsStateFragment;
}

const selector = (state: StoreState) =>
  state.routeAgreementDetails.addNameClicked;

export const EditRouteAgreementForm: React.FC<Props> = ({
  value,
  setFields,
  children,
  disableType,
  routeAgreement,
}) => {
  const addNameClicked = useSelector(selector);
  const nameInputFieldRefHolder = useRef<HTMLInputElement>(null!);
  const otherThanSpecialIsSelected =
    value.peak || value.offPeak || value.shoulder;

  useEffect(() => {
    if (nameInputFieldRefHolder.current) {
      nameInputFieldRefHolder.current.focus();
    }
  }, [addNameClicked]);

  return (
    <CardBody>
      <Row alignItems={"flex-start"}>
        <Box>
          <Text
            color={cssColor("--tjara")}
            variant={"bold"}
            size={"small"}
            whiteSpace={"nowrap"}
          >
            {"Select route"}
          </Text>
          <Space />
          <Row alignItems={"center"}>
            <RouteInRoutePairSelect
              value={value.routeIds}
              onValueChange={(routeIds) => setFields({ routeIds })}
              closeMenuOnSelect={false}
              includePartnerRoutes={value.includePartnerRoutes}
              isDisabled={
                routeAgreement?.statusCode === HeadAgreementStatusCode.Active
              }
            />
            <Space num={SpaceValues.SIXTEEN} />
            <CheckboxWithLabel
              label={"Include partner routes"}
              value={value.includePartnerRoutes}
              disabled={
                routeAgreement?.statusCode === HeadAgreementStatusCode.Active
              }
              onValueChange={(includePartnerRoutes) =>
                setFields({ includePartnerRoutes })
              }
            />
          </Row>
        </Box>
      </Row>
      <Space num={SpaceValues.THIRTYTWO} />
      <Label text={"Name"} optional={true}>
        <Box width={"500px"}>
          <TextInput
            inputRef={nameInputFieldRefHolder}
            value={value.name}
            onValueChange={(name) => setFields({ name })}
            data-testid={testIdConstants.createRouteAgreementNameTextBox}
          />
        </Box>
      </Label>
      <Space num={SpaceValues.THIRTYTWO} />
      <Txt
        color={cssColor("--lhds-color-ui-600")}
        size={"small"}
        whiteSpace={"nowrap"}
      >
        Validity
      </Txt>
      <Space />
      <Box width={"fit-content"}>
        <DateRangeDualTextInput
          value={{
            endDate: value.valid.endDate
              ? new Date(value.valid.endDate ?? "")
              : undefined,
            startDate: value.valid.startDate
              ? new Date(value.valid.startDate)
              : undefined,
          }}
          zIndex={ZIndex.high}
          onValueChange={(valid) =>
            setFields({
              valid: {
                endDate: valid.endDate?.toString(),
                startDate: valid.startDate?.toString(),
              },
            })
          }
        />
      </Box>
      <Space num={SpaceValues.THIRTYTWO} />
      <FieldGroupCaption label={"Agreement currency"}>
        <CurrencyRadioButtonRow
          value={value.currencyId}
          onValueChange={(currencyId) => setFields({ currencyId })}
          disabled={
            routeAgreement?.statusCode === HeadAgreementStatusCode.Active
          }
        />
      </FieldGroupCaption>
      <Space num={SpaceValues.THIRTYTWO} />
      <FieldGroupCaption label={"Type"}>
        <Row alignItems={"center"}>
          <RadioButtonWithLabel
            label={"Conditional"}
            checked={value.type === RouteAgreementTypeCode.Cond}
            onValueChange={() =>
              setFields({ type: RouteAgreementTypeCode.Cond })
            }
            disabled={disableType}
          />
          <Space num={SpaceValues.TWENTYFOUR} />
          <RadioButtonWithLabel
            label={"Base"}
            checked={value.type === RouteAgreementTypeCode.Base}
            onValueChange={() =>
              setFields({ type: RouteAgreementTypeCode.Base })
            }
            disabled={disableType}
          />
        </Row>
      </FieldGroupCaption>
      <Space num={SpaceValues.THIRTYTWO} />
      <FieldGroupCaption label={"Sailing cat."}>
        <Row alignItems={"center"}>
          <CheckboxWithLabel
            label={"Peak"}
            value={value.peak}
            disabled={
              value.special ||
              routeAgreement?.statusCode === HeadAgreementStatusCode.Active
            }
            onValueChange={(peak) => setFields({ peak })}
            data-testid={
              testIdConstants.createRouteAgreementSailingCategoryPeak
            }
          />
          <Space num={SpaceValues.TWENTYFOUR} />
          <CheckboxWithLabel
            label={"Off-Peak"}
            value={value.offPeak}
            disabled={
              value.special ||
              routeAgreement?.statusCode === HeadAgreementStatusCode.Active
            }
            onValueChange={(offPeak) => setFields({ offPeak })}
            data-testid={
              testIdConstants.createRouteAgreementSailingCategoryOffpeak
            }
          />
          <Space num={SpaceValues.TWENTYFOUR} />
          <CheckboxWithLabel
            label={"Shoulder"}
            value={value.shoulder}
            disabled={
              value.special ||
              routeAgreement?.statusCode === HeadAgreementStatusCode.Active
            }
            onValueChange={(shoulder) => setFields({ shoulder })}
            data-testid={
              testIdConstants.createRouteAgreementSailingCategoryShoulder
            }
          />
          <Indent num={IndentValues.TWENTYFOUR}>
            <Text color={cssColor("--lhds-color-ui-500")}>or</Text>
          </Indent>
          <CheckboxWithLabel
            label={"Special"}
            value={value.special}
            disabled={
              otherThanSpecialIsSelected ||
              routeAgreement?.statusCode === HeadAgreementStatusCode.Active
            }
            onValueChange={(special) => setFields({ special })}
          />
        </Row>
      </FieldGroupCaption>
      <Space num={SpaceValues.THIRTYTWO} />
      <Label text={"External description"} optional>
        <Box width={"500px"}>
          <TextArea
            rows={5}
            value={value.externalDescription}
            onValueChange={(externalDescription) =>
              setFields({ externalDescription })
            }
          />
        </Box>
      </Label>
      {children && (
        <>
          <Space num={SpaceValues.SIXTEEN} />
          {children}
        </>
      )}
    </CardBody>
  );
};
